/**
 * Ridimensiona un'immagine lato browser prima dell'upload.
 *
 * Serve per un motivo concreto: nginx davanti all'API rifiuta le richieste sopra
 * 1 MB, e una foto scattata col telefono ne pesa 3-5. Ridotta a 1600 px di lato
 * lungo in JPEG qualità 0.8 scende sotto i 500 KB restando perfettamente
 * leggibile — comprese le tacche di un metro a nastro in una foto di cantiere.
 *
 * I file non immagine passano invariati.
 */

/** Lato lungo massimo, in pixel. */
const MAX_SIDE = 1600;
/** Qualità JPEG in uscita. */
const QUALITY = 0.8;
/** Sotto questa soglia l'immagine passa così com'è. */
const SKIP_UNDER_BYTES = 600 * 1024;

export interface FilePayload {
  data: string; // base64 senza prefisso data URL
  mediaType: string;
  filename: string;
  size: number;
}

function stripDataUrl(dataUrl: string): string {
  return dataUrl.replace(/^data:[^;]+;base64,/, '');
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Lettura del file non riuscita'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Immagine non leggibile'));
    img.src = src;
  });
}

/**
 * Prepara un file per l'upload, ridimensionandolo se è un'immagine grande.
 * In caso di errore nel ridimensionamento si ripiega sul file originale:
 * meglio un upload pesante che un upload fallito.
 */
export async function prepareFileForUpload(file: File): Promise<FilePayload> {
  const originale = async (): Promise<FilePayload> => ({
    data: stripDataUrl(await readAsDataUrl(file)),
    mediaType: file.type,
    filename: file.name,
    size: file.size,
  });

  if (!file.type.startsWith('image/') || file.size <= SKIP_UNDER_BYTES) {
    return originale();
  }

  try {
    const img = await loadImage(await readAsDataUrl(file));

    const scala = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const w = Math.round(img.width * scala);
    const h = Math.round(img.height * scala);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return originale();

    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
    const base64 = stripDataUrl(dataUrl);

    // Se per qualche motivo il "ridimensionato" è più grande, tieni l'originale.
    const nuovaDimensione = Math.round((base64.length * 3) / 4);
    if (nuovaDimensione >= file.size) return originale();

    return {
      data: base64,
      mediaType: 'image/jpeg',
      filename: file.name.replace(/\.[^.]+$/, '') + '.jpg',
      size: nuovaDimensione,
    };
  } catch {
    return originale();
  }
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
